import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../../i18n/index.js';
import { playSound } from '../../audio/sounds.js';
import { GameIcon } from '../../components/GameIcon.js';
import { RulesDialog } from '../../components/RulesDialog.js';
import { SnakeBoard, SnakePad, ThemeSwitch, useSnakeControls, useSnakeTheme } from './SnakeBoard.js';
import {
  SNAKE_COUNTDOWN_MS,
  SNAKE_TICK_MS,
  SNAKE_W,
  initialState,
  queueTurn,
  step,
  type SnakeDir,
  type SnakeState,
} from '../../../shared/games/snake/index.js';

const BEST_KEY = 'areena.snakeBest';
const rand = (n: number) => Math.floor(Math.random() * n);
const key = (c: { x: number; y: number }) => c.y * SNAKE_W + c.x;

function readBest(): number {
  try {
    return Number(localStorage.getItem(BEST_KEY)) || 0;
  } catch {
    return 0;
  }
}

/**
 * Snake alone (decision 2026-09-30): practice in the browser with the same rules as the
 * two-player game. No statistics; the best score is kept in this browser only.
 */
export function SnakeSolo(props: { onExit: () => void }) {
  const [theme, setTheme] = useSnakeTheme();
  const game = useRef<SnakeState>(initialState(1, rand));
  const [phase, setPhase] = useState<'ready' | 'countdown' | 'playing' | 'over'>('ready');
  const [count, setCount] = useState(0);
  const [, redraw] = useState(0);
  const [best, setBest] = useState(readBest);
  const [newBest, setNewBest] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const area = useRef<HTMLDivElement>(null);

  const start = () => {
    game.current = initialState(1, rand);
    setNewBest(false);
    setPhase('countdown');
    setCount(Math.round(SNAKE_COUNTDOWN_MS / 1000));
    redraw((n) => n + 1);
  };

  const turn = (dir: SnakeDir) => {
    if (phase === 'playing' || phase === 'countdown') queueTurn(game.current.snakes[0]!, dir);
  };
  useSnakeControls(turn, area, phase === 'playing' || phase === 'countdown');

  // Space or Enter starts a new game.
  useEffect(() => {
    if (phase !== 'ready' && phase !== 'over') return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        start();
      }
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [phase]);

  // 3–2–1.
  useEffect(() => {
    if (phase !== 'countdown') return;
    if (count <= 0) {
      setPhase('playing');
      return;
    }
    playSound('snakeCount');
    const timer = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [phase, count]);

  // The game loop: one step every SNAKE_TICK_MS, timed from the start so the pace stays even.
  useEffect(() => {
    if (phase !== 'playing') return;
    const startedAt = performance.now();
    let steps = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      const s = game.current;
      const result = step(s, rand);
      steps++;
      if (result.ate.length > 0) playSound('snakeEat');
      redraw((n) => n + 1);
      if (!s.snakes[0]!.alive) {
        playSound('snakeCrash');
        const score = s.snakes[0]!.score;
        if (score > readBest()) {
          try {
            localStorage.setItem(BEST_KEY, String(score));
          } catch {
            // Not stored (private window).
          }
          setBest(score);
          setNewBest(score > 0);
        }
        setPhase('over');
        return;
      }
      timer = setTimeout(tick, Math.max(0, startedAt + (steps + 1) * SNAKE_TICK_MS - performance.now()));
    };
    timer = setTimeout(tick, SNAKE_TICK_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  const s = game.current;
  const me = s.snakes[0]!;
  const overlay =
    phase === 'countdown' ? (count > 0 ? String(count) : t.snake.go) : phase === 'over' ? t.snake.crashed : null;

  return (
    <section class="game-view">
      <div class="game-head">
        <h2>
          <GameIcon game="snake" size={30} /> {t.snake.soloTitle}
        </h2>
        <button class="btn btn-small" onClick={() => setShowRules(true)}>
          📖 {t.rules.buttonShort}
        </button>
        <button class="btn btn-small" onClick={props.onExit}>
          {t.snake.back}
        </button>
      </div>
      <div class="snake">
        <div class="snake-hud">
          <div class="snake-score mine">
            {t.snake.apples}: <strong>{me.score}</strong>
          </div>
          <div class="snake-score">
            {t.snake.best}: <strong>{best}</strong>
          </div>
        </div>
        <div class="snake-stage" ref={area}>
          <SnakeBoard
            theme={theme}
            apples={s.apples.map(key)}
            snakes={[{ body: me.body.map(key), dir: me.queue[me.queue.length - 1] ?? me.dir, alive: me.alive, mine: true }]}
            dim={phase !== 'playing'}
          />
          {overlay && phase !== 'over' && <div class="snake-overlay">{overlay}</div>}
          {(phase === 'ready' || phase === 'over') && (
            <div class="snake-overlay snake-menu">
              {phase === 'over' && <div class="snake-menu-title">{newBest ? t.snake.newBest : t.snake.soloOver(me.score)}</div>}
              <button class="btn btn-primary" onClick={start} autofocus>
                {phase === 'over' ? t.snake.soloAgain : t.snake.soloStart}
              </button>
              <div class="snake-menu-hint">{t.snake.soloHint}</div>
              {/* The theme is chosen before the game, not while playing (user request 2026-09-30). */}
              <ThemeSwitch theme={theme} onChange={setTheme} />
            </div>
          )}
        </div>
        <SnakePad onTurn={turn} disabled={phase !== 'playing' && phase !== 'countdown'} />
      </div>
      {showRules && <RulesDialog game="snake" onClose={() => setShowRules(false)} />}
    </section>
  );
}
